/**
 * Country / administrative-region / operating-market domain (S007, ADR-0005).
 *
 * These are the typed operational units of the resolution hierarchy. A city is **never** a free-form
 * universal string: it is an `OperatingMarket` with a branded `MarketId`. Versioning, effective-dating,
 * scope resolution, override permissions, and immutable snapshots are the successor session S008; here the
 * `CountryPack` carries the versioned/effective-dated/audited *shape* those mechanics will consume.
 */
import { ok, err, isIso4217Currency } from '@vello/core';
const ALPHA2 = /^[A-Z]{2}$/; // ISO 3166-1 alpha-2
const REGION = /^[A-Z]{2}-[A-Z0-9]{1,3}$/; // ISO 3166-2 subdivision, e.g. BR-SP
const MARKET = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/; // "cc:slug", e.g. br:sao-paulo
const ISO_4217 = /^[A-Z]{3}$/;
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
/** ISO 3166-1 alpha-2, e.g. `BR`. */
export function countryCode(code) {
    if (!ALPHA2.test(code))
        throw new RangeError(`invalid ISO 3166-1 alpha-2 country code: ${code}`);
    return code;
}
/** ISO 3166-2 subdivision, e.g. `BR-SP`. */
export function regionCode(code) {
    if (!REGION.test(code))
        throw new RangeError(`invalid ISO 3166-2 region code: ${code}`);
    return code;
}
/** Typed market id `cc:slug`, e.g. `br:sao-paulo`. Rejects free-form city names so a city can never be a
 *  loose string. */
export function marketId(id) {
    if (!MARKET.test(id))
        throw new RangeError(`invalid market id (expected "cc:slug"): ${id}`);
    return id;
}
function assertUtcInstant(value, field) {
    if (!UTC_INSTANT.test(value) || Number.isNaN(Date.parse(value))) {
        throw new RangeError(`${field} must be a UTC ISO-8601 instant ending in Z: ${value}`);
    }
}
function isValidTimeZone(tz) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Operating-market lifecycle (REQ-CAT-045, operating-model §3.4). Canonical seven-state machine; legal
 * transitions and launch gates are enforced by the S014 governance workflow (`@vello/governance`).
 */
export const MARKET_LAUNCH_STATES = [
    'DRAFT',
    'CONFIGURING',
    'PILOT',
    'ACTIVE',
    'PAUSED',
    'EXITING',
    'RETIRED',
];
/**
 * W7-OPS-GOVERNANCE (D420; owner decision D408 #9): why an operator changed a market's lifecycle status. A closed,
 * stable vocabulary: the reason is persisted on the market's audit row with the change, in the same transaction.
 * (Four eyes on `ACTIVE` belongs to launch governance, later — D408 #9.)
 */
export const MARKET_STATUS_REASON_CODES = [
    'market_launch_approved',
    'market_pilot_extended',
    'market_paused_by_operations',
    'market_regulatory_hold',
    'market_retired_end_of_service',
    'market_other',
];
/**
 * W7-OPS-GOVERNANCE (D420; owner decisions D408 #12 and D422): why a country pack is retired (`APPROVED → RETIRED`).
 * A closed, stable vocabulary carried by the maker's proposal and the audit rows; the free-text reason travels beside it.
 */
export const COUNTRY_PACK_RETIRE_REASON_CODES = [
    'pack_country_exit',
    'pack_registered_in_error',
    'pack_test_registration',
    'pack_superseded',
    'pack_other',
];
function assertGeoPoint(p) {
    if (!Number.isFinite(p.latitude) || p.latitude < -90 || p.latitude > 90) {
        throw new RangeError(`latitude must be a finite value within [-90, 90]: ${p.latitude}`);
    }
    if (!Number.isFinite(p.longitude) || p.longitude < -180 || p.longitude > 180) {
        throw new RangeError(`longitude must be a finite value within [-180, 180]: ${p.longitude}`);
    }
}
export function operatingMarket(input) {
    if (!isValidTimeZone(input.timezone)) {
        throw new RangeError(`invalid IANA timezone: ${input.timezone}`);
    }
    if (input.center !== undefined)
        assertGeoPoint(input.center);
    return { ...input };
}
export function marketLaunchState(marketIdValue, status, since) {
    if (!MARKET_LAUNCH_STATES.includes(status)) {
        throw new RangeError(`invalid market launch status: ${status}`);
    }
    assertUtcInstant(since, 'since');
    return { marketId: marketIdValue, status, since };
}
export function countryPack(input) {
    if (!Number.isInteger(input.version) || input.version < 1) {
        throw new RangeError(`country pack version must be an integer >= 1: ${input.version}`);
    }
    if (!Number.isInteger(input.schemaVersion) || input.schemaVersion < 1) {
        throw new RangeError(`country pack schemaVersion must be an integer >= 1: ${input.schemaVersion}`);
    }
    if (!ISO_4217.test(input.currency) || !isIso4217Currency(input.currency)) {
        // Shape (`^[A-Z]{3}$`) then the assigned-code allowlist: a well-formed but UNASSIGNED code (`AAA`) is
        // rejected here, where the pack is minted, even though the DB CHECK stays shape-only.
        throw new RangeError(`currency must be an assigned ISO 4217 alpha-3 code: ${input.currency}`);
    }
    assertUtcInstant(input.effectiveFrom, 'effectiveFrom');
    if (input.effectiveTo !== undefined) {
        assertUtcInstant(input.effectiveTo, 'effectiveTo');
        if (Date.parse(input.effectiveTo) <= Date.parse(input.effectiveFrom)) {
            throw new RangeError('effectiveTo must be after effectiveFrom');
        }
    }
    assertUtcInstant(input.audit.createdAt, 'audit.createdAt');
    // Governance gate (finding #5): an APPROVED pack is only valid with a full approval record — approver,
    // reason, and a UTC approval instant. A pack that would RESOLVE onto a transaction must be accountable.
    if (input.status === 'APPROVED') {
        if (input.audit.approvedBy === undefined || input.audit.approvedBy.trim() === '') {
            throw new RangeError('an APPROVED country pack requires audit.approvedBy');
        }
        if (input.audit.approvalReason === undefined || input.audit.approvalReason.trim() === '') {
            throw new RangeError('an APPROVED country pack requires audit.approvalReason');
        }
        if (input.audit.approvedAt === undefined) {
            throw new RangeError('an APPROVED country pack requires audit.approvedAt');
        }
        assertUtcInstant(input.audit.approvedAt, 'audit.approvedAt');
    }
    // A pack always advertises at least its recommended locale, so consumers never see an empty supported set.
    return { ...input, supportedLocales: input.supportedLocales ?? [input.locale] };
}
/**
 * Resolve the pack that GOVERNS a country at an instant (ADR-0005 governance; the country tier of the
 * resolution hierarchy). **Only** a pack whose `status === 'APPROVED'` AND whose effective window contains
 * `at` (`effectiveFrom <= at < effectiveTo`) resolves. A `DRAFT` or `RETIRED` pack, or an `APPROVED` pack
 * outside its window, does **not** — the caller fails closed (no governing pack ⇒ deny; never a silent
 * fall-through to an ungoverned default). When multiple APPROVED packs are effective at once (a
 * misconfiguration) the highest `version` wins so resolution is deterministic. Pure and side-effect-free.
 */
export function resolveCountryPack(packs, country, at) {
    const atMs = Date.parse(at);
    if (Number.isNaN(atMs))
        throw new RangeError(`at must be a valid instant: ${at}`);
    let winner;
    for (const pack of packs) {
        if (pack.countryCode !== country)
            continue;
        if (pack.status !== 'APPROVED')
            continue; // DRAFT / RETIRED never governs a transaction
        const from = Date.parse(pack.effectiveFrom);
        const to = pack.effectiveTo === undefined ? Infinity : Date.parse(pack.effectiveTo);
        if (!(from <= atMs && atMs < to))
            continue; // outside the effective window
        if (winner === undefined || pack.version > winner.version)
            winner = pack;
    }
    return winner === undefined
        ? { reason: 'NO_APPROVED_EFFECTIVE_PACK' }
        : { reason: 'RESOLVED', pack: winner };
}
export const HIERARCHY_REASONS = {
    DUPLICATE_COUNTRY: 'DUPLICATE_COUNTRY',
    DUPLICATE_REGION: 'DUPLICATE_REGION',
    DUPLICATE_MARKET: 'DUPLICATE_MARKET',
    REGION_UNKNOWN_COUNTRY: 'REGION_UNKNOWN_COUNTRY',
    REGION_COUNTRY_PREFIX_MISMATCH: 'REGION_COUNTRY_PREFIX_MISMATCH',
    MARKET_UNKNOWN_COUNTRY: 'MARKET_UNKNOWN_COUNTRY',
    MARKET_UNKNOWN_REGION: 'MARKET_UNKNOWN_REGION',
    MARKET_REGION_COUNTRY_MISMATCH: 'MARKET_REGION_COUNTRY_MISMATCH',
    MARKET_ID_COUNTRY_MISMATCH: 'MARKET_ID_COUNTRY_MISMATCH',
};
/**
 * Validate referential integrity of a country/region/market registry. Returns the registry on success or
 * the full list of reason-coded issues — never throws for a data problem (throwing is reserved for
 * malformed primitive values at their smart constructors).
 */
export function validateHierarchy(reg) {
    const issues = [];
    const countries = new Set();
    for (const c of reg.countries) {
        if (countries.has(c.code))
            issues.push({ reason: HIERARCHY_REASONS.DUPLICATE_COUNTRY, ref: c.code });
        countries.add(c.code);
    }
    const regions = new Map();
    for (const r of reg.regions) {
        if (regions.has(r.code))
            issues.push({ reason: HIERARCHY_REASONS.DUPLICATE_REGION, ref: r.code });
        regions.set(r.code, r);
        if (!countries.has(r.countryCode)) {
            issues.push({ reason: HIERARCHY_REASONS.REGION_UNKNOWN_COUNTRY, ref: r.code, detail: r.countryCode });
        }
        if (!r.code.startsWith(`${r.countryCode}-`)) {
            issues.push({
                reason: HIERARCHY_REASONS.REGION_COUNTRY_PREFIX_MISMATCH,
                ref: r.code,
                detail: r.countryCode,
            });
        }
    }
    const markets = new Set();
    for (const m of reg.markets) {
        if (markets.has(m.id))
            issues.push({ reason: HIERARCHY_REASONS.DUPLICATE_MARKET, ref: m.id });
        markets.add(m.id);
        if (!countries.has(m.countryCode)) {
            issues.push({ reason: HIERARCHY_REASONS.MARKET_UNKNOWN_COUNTRY, ref: m.id, detail: m.countryCode });
        }
        if (m.id.slice(0, 2) !== m.countryCode.toLowerCase()) {
            issues.push({ reason: HIERARCHY_REASONS.MARKET_ID_COUNTRY_MISMATCH, ref: m.id, detail: m.countryCode });
        }
        if (m.regionCode !== undefined) {
            const region = regions.get(m.regionCode);
            if (!region) {
                issues.push({ reason: HIERARCHY_REASONS.MARKET_UNKNOWN_REGION, ref: m.id, detail: m.regionCode });
            }
            else if (region.countryCode !== m.countryCode) {
                issues.push({
                    reason: HIERARCHY_REASONS.MARKET_REGION_COUNTRY_MISMATCH,
                    ref: m.id,
                    detail: m.regionCode,
                });
            }
        }
    }
    return issues.length === 0 ? ok(reg) : err(issues);
}
/**
 * Validate that a resolution context is internally consistent along the country ancestry: a market or region
 * named alongside a country must belong to that country, and a market named alongside a region must share its
 * country. A market id encodes its country as the `cc:` prefix and a region as the `CC-` prefix, so ancestry
 * is a prefix agreement — no registry lookup is needed to reject a cross-country context (e.g. `country=AA`
 * with a `bb:*` market, where market `bb` belongs to a different country). Under-specified contexts (a higher
 * scope absent) are consistent by construction; only the present pairs are checked.
 *
 * Pure: returns a stable {@link HierarchyReason} for the first inconsistency, or `null` when consistent. This
 * is the ancestry guard the S008 resolution path calls so a lower scope can never compose over the wrong
 * country (ADR-0005 market isolation).
 */
export function validateContextAncestry(ctx) {
    // Country prefix each lower scope declares: "bb" from "bb:slug", "br" from region "BR-SP".
    const marketCc = ctx.market !== undefined ? ctx.market.slice(0, 2).toLowerCase() : undefined;
    const regionCc = ctx.region !== undefined ? ctx.region.slice(0, 2).toLowerCase() : undefined;
    if (ctx.country !== undefined) {
        const cc = ctx.country.toLowerCase();
        if (marketCc !== undefined && marketCc !== cc)
            return HIERARCHY_REASONS.MARKET_ID_COUNTRY_MISMATCH;
        if (regionCc !== undefined && regionCc !== cc)
            return HIERARCHY_REASONS.REGION_COUNTRY_PREFIX_MISMATCH;
    }
    if (marketCc !== undefined && regionCc !== undefined && marketCc !== regionCc) {
        return HIERARCHY_REASONS.MARKET_REGION_COUNTRY_MISMATCH;
    }
    return null;
}
//# sourceMappingURL=country.js.map